import { Controller } from "@hotwired/stimulus"

// RL-131b: the analyzing screen's client resilience. The server render + job
// broadcasts are the source of truth (contract 6: full-element replace of
// #analysis_progress from a fresh DB read); everything here is a FALLBACK so the
// user still reaches a terminal state if a broadcast is lost, arrives out of
// order, or the tab was backgrounded while it fired:
//
//   - poll the show action every POLL_MS and follow its terminal redirect
//   - re-fetch immediately on visibilitychange (background tabs throttle timers)
//   - an absolute deadline derived from the SERVER-stamped requested-at (not a
//     client-only timer), which confirms server state once before giving up
//   - a `terminal` latch so completion and the deadline can't both fire
//   - disconnect() tears down the timer + listener; every async callback bails
//     if the controller was disconnected mid-flight (contract 7)
export default class extends Controller {
  static targets = ["status"]
  static values = {
    pollUrl: String,
    requestedAt: String,
    deadlineSeconds: { type: Number, default: 180 }
  }

  POLL_MS = 5000

  connect() {
    this.terminal = false
    this.disconnected = false

    // If the server already rendered a terminal state (redirect race), the page
    // will navigate on its own; still latch so we never double-fire.
    if (this.statusTarget?.dataset.terminal === "true") {
      this.terminal = true
      return
    }

    this.deadlineAt = this.computeDeadline()
    this.pollTimer = setInterval(() => this.poll(), this.POLL_MS)
    this.onVisibility = () => {
      if (document.visibilityState === "visible") this.poll()
    }
    document.addEventListener("visibilitychange", this.onVisibility)
  }

  disconnect() {
    this.disconnected = true
    if (this.pollTimer) clearInterval(this.pollTimer)
    if (this.onVisibility) {
      document.removeEventListener("visibilitychange", this.onVisibility)
    }
  }

  computeDeadline() {
    // Absolute deadline anchored to the server timestamp, not connect() time, so
    // a tab that was backgrounded for minutes still expires on schedule.
    const started = Date.parse(this.requestedAtValue)
    const base = Number.isNaN(started) ? Date.now() : started
    return base + this.deadlineSecondsValue * 1000
  }

  async poll() {
    if (this.terminal || this.disconnected) return

    try {
      const response = await fetch(this.pollUrlValue, {
        headers: { Accept: "text/html" },
        redirect: "follow"
      })
      if (this.disconnected) return

      // The show action 302s into the coach shell once terminal; a fetch that
      // followed the redirect lands on a different URL — navigate the page there.
      if (response.redirected && response.url) {
        this.finish(response.url)
        return
      }
    } catch (_e) {
      // Network blip — the next poll (or a broadcast) recovers. Fall through to
      // the deadline check so a persistently dead server still terminates.
    }

    if (Date.now() >= this.deadlineAt) this.timeout()
  }

  async timeout() {
    if (this.terminal || this.disconnected) return

    // Confirm server state ONCE before declaring failure — the analysis may have
    // completed while a broadcast was dropped and every poll raced past it.
    try {
      const response = await fetch(this.pollUrlValue, {
        headers: { Accept: "text/html" },
        redirect: "follow"
      })
      if (this.disconnected) return
      if (response.redirected && response.url) {
        this.finish(response.url)
        return
      }
    } catch (_e) {
      // ignore — fall through to the reload below
    }

    // Server still says non-terminal past the deadline: reload the page so the
    // server render decides. If it stamped `failed` by now, show() redirects into
    // the coach with the recovery message.
    this.finish(this.pollUrlValue)
  }

  finish(url) {
    if (this.terminal) return
    this.terminal = true
    if (this.pollTimer) clearInterval(this.pollTimer)
    window.location.assign(url)
  }
}
